import { Controller } from "@hotwired/stimulus"

import { maskForInput, normalizePrefilled, normalizeState } from "../lib/input_format.mjs"

// Connects to data-controller="input-format"
//
// Live masking + light validation for member-facing charity-care form inputs, keyed off a "kind"
// derived server-side from the field's canonical name / type / label (CharityCareApplicationField#input_format).
// This generalizes across every form: any phone/ssn/zip/state field gets consistent formatting.
// The server re-normalizes on save (CharityCareApplicationField#normalize_input_value), so this is
// purely a UX nicety — it never has to be the only line of defense.
//
//   kind="phone" -> (xxx) xxx-xxxx        digits only, progressive
//   kind="ssn"   -> xxx-xx-xxxx           digits only, progressive
//   kind="zip"   -> 12345 or 12345-6789   digits only
//   kind="npi"   -> 10 digits             digits only, capped
//   kind="ein"   -> xx-xxxxxxx            digits only, progressive (a typed dash is absorbed)
//   kind="state" -> 2-letter USPS code    uppercases; maps full names on blur
//
// Prefilled values are normalized conservatively on connect: only a confidently-recognized value is
// rewritten, so loading + submitting a form can never truncate/blank an existing value (see the
// helpers in ../lib/input_format.mjs, which mirror the server's conservative behavior).
export default class extends Controller {
  static values = { kind: String }

  connect() {
    this.onInput = this.handleInput.bind(this)
    this.onBlur = this.handleBlur.bind(this)
    this.element.addEventListener("input", this.onInput)
    this.element.addEventListener("blur", this.onBlur)
    this.setValue(normalizePrefilled(this.kindValue, this.element.value))
  }

  disconnect() {
    this.element.removeEventListener("input", this.onInput)
    this.element.removeEventListener("blur", this.onBlur)
  }

  handleInput() {
    this.setValue(maskForInput(this.kindValue, this.element.value))
  }

  handleBlur() {
    if (this.kindValue === "state") {
      this.setValue(normalizeState(this.element.value))
    }
  }

  setValue(next) {
    if (next !== this.element.value) {
      this.element.value = next
    }
  }
}
